import { useEffect, useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { QRCodeSVG } from 'qrcode.react'
import Layout from '@/components/Layout'
import DateTimePicker from '@/components/DateTimePicker'
import AdminDialog from '@/components/AdminDialog'
import { openFromRow } from '@/lib/openFromRow'
import { FilterSelect } from '@/components/FilterDropdown'
import {
  faceAuthApi,
  type FaceAuthSession,
  type FaceAuthGroup,
  type GroupRegistration,
} from '@/api/faceAuth'
import { errorMessage } from '@/api/client'

const field =
  'mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 outline-none focus:border-brand-600 focus:ring-2 focus:ring-brand-100'
const button =
  'rounded-xl bg-brand-600 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-50'
function time(value: string | null) {
  return value
    ? new Date(value).toLocaleString('zh-CN', { hour12: false })
    : '暂未提供'
}
function timestamp(value: FormDataEntryValue | null) {
  return typeof value === 'string' && value
    ? new Date(value).toISOString()
    : undefined
}

export default function FaceAuthPage() {
  const [panel, setPanel] = useState<'create' | 'register'>()
  const [detail, setDetail] = useState<FaceAuthGroup>()
  const cache = useQueryClient()
  const [name, setName] = useState('')
  const [session, setSession] = useState<FaceAuthSession>()
  const [expired, setExpired] = useState(false)
  const [notice, setNotice] = useState('')
  const [copyError, setCopyError] = useState('')
  const [page, setPage] = useState(1)
  const [status, setStatus] = useState('active')
  const [disableTarget, setDisableTarget] = useState<FaceAuthGroup>()
  const [disableReason, setDisableReason] = useState('')
  const groups = useQuery({
    queryKey: ['face-auth-groups', page, status],
    queryFn: () => faceAuthApi.groups(page, status),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: 'always',
  })
  const create = useMutation({
    mutationFn: () => faceAuthApi.create(name.trim()),
    onSuccess: (value) => {
      setSession(value)
      setExpired(false)
      setNotice('认证链接已生成，请交给艺人本人完成认证。')
      setCopyError('')
    },
  })
  const register = useMutation({
    mutationFn: (input: GroupRegistration) => faceAuthApi.register(input),
    onSuccess: () => {
      setPanel(undefined)
      setNotice('已有授权组已登记，后台将持续同步组内素材。')
      void cache.invalidateQueries({ queryKey: ['face-auth-groups'] })
    },
  })
  const sync = useMutation({
    mutationFn: faceAuthApi.sync,
    onSuccess: (value) => {
      setNotice(
        `同步完成：读取 ${value.fetched} 条，新增 ${value.imported} 条，更新 ${value.updated} 条。`,
      )
      void cache.invalidateQueries({ queryKey: ['face-auth-groups'] })
    },
  })
  const disable = useMutation({
    mutationFn: faceAuthApi.disable,
    onSuccess: () => {
      setDisableTarget(undefined)
      setNotice('授权组已停用，用户将无法继续使用该组素材。')
      void cache.invalidateQueries({ queryKey: ['face-auth-groups'] })
    },
  })
  useEffect(() => {
    if (!session) return
    const remaining = Date.parse(session.expires_at) - Date.now()
    if (remaining <= 0) {
      setExpired(true)
      return
    }
    const timer = window.setTimeout(() => setExpired(true), remaining)
    return () => window.clearTimeout(timer)
  }, [session])
  function submit(event: FormEvent) {
    event.preventDefault()
    if (!create.isPending && name.trim()) {
      setSession(undefined)
      setNotice('')
      create.mutate()
    }
  }
  function submitGroup(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (register.isPending) return
    const values = new FormData(event.currentTarget)
    register.mutate({
      group_id: String(values.get('group_id') || '').trim(),
      artist_label: String(values.get('artist_label') || '').trim(),
      authorized_at: timestamp(values.get('authorized_at')),
      authorization_starts_at: timestamp(values.get('authorization_starts_at')),
      authorization_expires_at: timestamp(
        values.get('authorization_expires_at'),
      ),
    })
  }
  async function copy() {
    if (!session || expired) return
    try {
      await navigator.clipboard.writeText(session.h5_url)
      setNotice('认证链接已复制')
      setCopyError('')
    } catch {
      setCopyError('无法访问剪贴板，请选中下方链接手动复制。')
    }
  }
  const failure = create.error || register.error || sync.error || disable.error
  return (
    <Layout>
      <div className="flex min-h-0 flex-1 flex-col gap-4">
        <header className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">
              人脸授权管理
            </h1>
            <p className="mt-2 text-sm text-slate-500">
              查看授权期限和素材同步结果，发起或登记艺人授权。
            </p>
          </div>
          <div className="flex gap-3">
            <button
              className="rounded-lg border px-4 py-2 text-sm"
              onClick={(e) => {
                openFromRow(e)
                register.reset()
                setPanel('register')
              }}
            >
              登记已有授权组
            </button>
            <button
              className={button}
              onClick={(e) => {
                openFromRow(e)
                create.reset()
                setPanel('create')
              }}
            >
              发起认证
            </button>
          </div>
        </header>
        {notice && (
          <p
            role="status"
            className="rounded-xl bg-emerald-50 p-4 text-sm text-emerald-800"
          >
            {notice}
          </p>
        )}
        {failure && (
          <p
            role="alert"
            className="rounded-xl bg-red-50 p-4 text-sm text-red-700"
          >
            {errorMessage(failure)}
          </p>
        )}
        {panel === 'create' && (
          <AdminDialog
            title="发起艺人认证"
            busy={create.isPending}
            onClose={() => setPanel(undefined)}
          >
            {create.error && (
              <p role="alert" className="text-sm text-red-700">
                {errorMessage(create.error)}
              </p>
            )}
            <form
              onSubmit={submit}
              className="mt-4 flex flex-col gap-4 sm:flex-row sm:items-end"
            >
              <label className="flex-1 text-sm font-medium">
                艺人姓名
                <input
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  maxLength={128}
                  required
                  autoComplete="off"
                  className={field}
                  placeholder="请输入艺人姓名"
                />
              </label>
              <button
                className={button}
                disabled={create.isPending || !name.trim()}
              >
                {create.isPending ? '正在生成…' : '确认发起'}
              </button>
            </form>
            {session && (
              <div className="mt-6 flex flex-col gap-6 border-t border-slate-100 pt-6 sm:flex-row">
                {!expired && (
                  <div
                    className="shrink-0 rounded-xl border border-slate-200 bg-white p-4"
                    aria-label="艺人认证二维码"
                  >
                    <QRCodeSVG
                      value={session.qr_payload || session.h5_url}
                      size={220}
                      level="M"
                      marginSize={2}
                    />
                  </div>
                )}
                <div className="min-w-0 flex-1 space-y-3">
                  <h3 className="font-medium">
                    {expired ? '认证链接已过期' : '请艺人本人扫码完成认证'}
                  </h3>
                  <p className="text-sm text-slate-500">
                    {expired
                      ? '请重新发起认证，生成新的链接。'
                      : '链接仅用于一次认证。发给艺人后，您可以离开页面；认证结果由后台自动处理。'}
                  </p>
                  <p className="text-sm text-slate-500">
                    链接有效至：{time(session.expires_at)}
                  </p>
                  {!expired && (
                    <>
                      <label className="block text-sm">
                        认证链接
                        <textarea
                          readOnly
                          value={session.h5_url}
                          rows={3}
                          className={`${field} break-all`}
                          onFocus={(event) => event.target.select()}
                        />
                      </label>
                      <button
                        type="button"
                        className={button}
                        onClick={() => void copy()}
                      >
                        复制认证链接
                      </button>
                    </>
                  )}
                  {copyError && (
                    <p role="alert" className="text-sm text-amber-700">
                      {copyError}
                    </p>
                  )}
                </div>
              </div>
            )}
          </AdminDialog>
        )}
        {panel === 'register' && (
          <AdminDialog
            title="登记已有授权组"
            busy={register.isPending}
            onClose={() => setPanel(undefined)}
          >
            <p className="mt-3 text-sm text-slate-500">
              用于火山控制台中已接收的授权组。每组登记一次即可；授权时间和期限请按已有授权档案填写，未知时留空。
            </p>
            {register.error && (
              <p role="alert" className="text-sm text-red-700">
                {errorMessage(register.error)}
              </p>
            )}
            <form
              onSubmit={submitGroup}
              className="mt-4 grid gap-4 sm:grid-cols-2"
            >
              <label className="text-sm">
                素材组 ID
                <input
                  name="group_id"
                  required
                  maxLength={128}
                  placeholder="group-…"
                  className={field}
                />
              </label>
              <label className="text-sm">
                艺人姓名
                <input
                  name="artist_label"
                  required
                  maxLength={128}
                  className={field}
                />
              </label>
              <DateTimePicker label="授权时间（选填）" name="authorized_at" />
              <DateTimePicker
                label="授权生效时间（选填）"
                name="authorization_starts_at"
              />
              <DateTimePicker
                label="授权截止时间（选填）"
                name="authorization_expires_at"
              />
              <button
                disabled={register.isPending}
                className={`${button} justify-self-end sm:col-span-2`}
              >
                {register.isPending ? '正在登记…' : '登记并同步'}
              </button>
            </form>
          </AdminDialog>
        )}
        <section className="flex min-h-0 flex-1 flex-col">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-lg font-semibold">已认证真人组</h2>
            <div className="flex items-end gap-3">
              <FilterSelect
                label="授权组状态"
                value={status}
                onChange={(value) => {
                  setStatus(value)
                  setPage(1)
                }}
                options={[
                  ['active', '启用中'],
                  ['disabled', '已停用'],
                  ['', '全部'],
                ]}
              />
              <button
                type="button"
                onClick={() => void groups.refetch()}
                disabled={groups.isFetching}
                className="text-sm text-brand-600"
              >
                {groups.isFetching ? '刷新中…' : '刷新列表'}
              </button>
            </div>
          </div>
          <p className="mt-2 text-sm text-slate-500">
            重新进入页面或点击刷新查看最新结果。认证成功后，组内已处理完成的图片会自动同步到用户端。
          </p>
          {groups.isPending ? (
            <p className="py-8 text-sm text-slate-500">正在加载授权组…</p>
          ) : groups.isError ? (
            <p role="alert" className="py-8 text-sm text-red-700">
              {errorMessage(groups.error)}
            </p>
          ) : (
            <>
              <div className="mt-4 min-h-64 flex-1 overflow-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
                <table className="w-full min-w-[900px] text-left text-sm">
                  <thead className="sticky top-0 bg-slate-100 text-slate-600">
                    <tr>
                      {[
                        '艺人 / 素材组',
                        '状态',
                        '授权有效期',
                        '素材数量',
                        '最近同步',
                        '操作',
                      ].map((label) => (
                        <th key={label} className="px-3 py-3 font-medium">
                          {label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {groups.data.groups.map((group) => {
                      const future =
                        !!group.authorization_starts_at &&
                        Date.parse(group.authorization_starts_at) > Date.now()
                      const ended =
                        !!group.authorization_expires_at &&
                        Date.parse(group.authorization_expires_at) <= Date.now()
                      return (
                        <tr
                          key={group.group_id}
                          className="border-b border-slate-100"
                        >
                          <td className="px-3 py-4">
                            <p className="font-medium">{group.artist_label}</p>
                            <p className="mt-1 text-xs text-slate-500 break-all">
                              {group.group_id}
                            </p>
                          </td>
                          <td className="px-3 py-4 whitespace-nowrap">
                            <span
                              className={
                                group.status === 'disabled'
                                  ? 'text-slate-500'
                                  : ended
                                    ? 'text-red-700'
                                    : future
                                      ? 'text-amber-700'
                                      : 'text-green-700'
                              }
                              title={group.disabled_reason || undefined}
                            >
                              {group.status === 'disabled'
                                ? '已停用'
                                : ended
                                  ? '已到期'
                                  : future
                                    ? '未生效'
                                    : '可用'}
                            </span>
                          </td>
                          <td className="px-3 py-4 text-xs">
                            <p>{time(group.authorization_starts_at)}</p>
                            <p className="mt-1 text-slate-500">
                              至 {time(group.authorization_expires_at)}
                            </p>
                          </td>
                          <td className="px-3 py-4">{group.asset_count}</td>
                          <td className="px-3 py-4">
                            {time(group.last_synced_at)}
                          </td>
                          <td className="space-x-4 whitespace-nowrap px-3 py-4">
                            <button
                              className="text-brand-600"
                              onClick={(e) => {
                                openFromRow(e)
                                setDetail(group)
                              }}
                            >
                              详情
                            </button>
                            <button
                              type="button"
                              disabled={
                                sync.isPending ||
                                group.status !== 'active' ||
                                future ||
                                ended
                              }
                              onClick={() => sync.mutate(group.group_id)}
                              className="text-brand-600 disabled:opacity-40"
                            >
                              {sync.isPending &&
                              sync.variables === group.group_id
                                ? '同步中…'
                                : '同步素材'}
                            </button>
                            {group.status === 'active' && (
                              <button
                                type="button"
                                className="text-red-600"
                                onClick={(e) => {
                                  openFromRow(e)
                                  setDisableTarget(group)
                                  setDisableReason('')
                                  disable.reset()
                                }}
                              >
                                停用
                              </button>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
              {!groups.data.groups.length && (
                <p className="py-8 text-center text-sm text-slate-500">
                  暂无授权组，可发起认证或登记已有授权组。
                </p>
              )}
              <div className="mt-4 flex items-center justify-between text-sm text-slate-500">
                <span>
                  共 {groups.data.total} 组 · 第 {page} 页
                </span>
                <div className="flex gap-4">
                  <button
                    disabled={page <= 1 || groups.isFetching}
                    onClick={() => setPage((value) => value - 1)}
                    className="disabled:opacity-40"
                  >
                    上一页
                  </button>
                  <button
                    disabled={
                      page * 20 >= groups.data.total || groups.isFetching
                    }
                    onClick={() => setPage((value) => value + 1)}
                    className="disabled:opacity-40"
                  >
                    下一页
                  </button>
                </div>
              </div>
            </>
          )}
        </section>
        {disableTarget && (
          <AdminDialog
            title="停用授权组"
            busy={disable.isPending}
            onClose={() => setDisableTarget(undefined)}
          >
            <form
              className="space-y-4"
              onSubmit={(event) => {
                event.preventDefault()
                if (!disable.isPending && disableReason.trim())
                  disable.mutate({
                    group_id: disableTarget.group_id,
                    reason: disableReason.trim(),
                  })
              }}
            >
              <h2 className="text-lg font-semibold">
                停用 {disableTarget.artist_label} 的授权组
              </h2>
              <p className="text-sm text-slate-500">
                停用后停止同步，该组素材将无法在用户端继续使用。
              </p>
              <label className="block text-sm">
                停用原因
                <input
                  required
                  maxLength={256}
                  value={disableReason}
                  onChange={(event) => setDisableReason(event.target.value)}
                  className={field}
                />
              </label>
              {disable.error && (
                <p role="alert" className="text-sm text-red-700">
                  {errorMessage(disable.error)}
                </p>
              )}
              <div className="flex justify-end gap-4">
                <button
                  type="button"
                  disabled={disable.isPending}
                  onClick={() => setDisableTarget(undefined)}
                >
                  取消
                </button>
                <button
                  className={button}
                  disabled={disable.isPending || !disableReason.trim()}
                >
                  {disable.isPending ? '正在停用…' : '确认停用'}
                </button>
              </div>
            </form>
          </AdminDialog>
        )}
        {detail && (
          <AdminDialog title="授权组详情" onClose={() => setDetail(undefined)}>
            <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-4 text-sm">
              {Object.entries({
                艺人姓名: detail.artist_label,
                '素材组 ID': detail.group_id,
                授权时间: time(detail.authorized_at),
                生效时间: time(detail.authorization_starts_at),
                截止时间: time(detail.authorization_expires_at),
                素材数量: String(detail.asset_count),
                最近同步: time(detail.last_synced_at),
                停用原因: detail.disabled_reason || '—',
              }).map(([k, v]) => (
                <div key={k} className="contents">
                  <dt className="text-slate-500">{k}</dt>
                  <dd className="break-all">{v}</dd>
                </div>
              ))}
            </dl>
          </AdminDialog>
        )}
      </div>
    </Layout>
  )
}
